import { ID, Task, UpdateGoal, UpdateTask } from '@griply/types';
import { buildTaskDeletionUpdates, collectRelatedTaskIds } from '@griply/utils';
import { useCallback } from 'react';

export type UseDeleteTasksOptions = {
  // one-shot fetch (not a subscription) resolving selected + related tasks at delete time
  getTasksByIds: (ids: ID[]) => Promise<Task[]>;
  updateTasksAsync: (updates: UpdateTask[]) => Promise<unknown>;
  updateGoal?: (params: { goal: UpdateGoal }) => void;
  onTaskDeleted?: (task: Task) => void;
};

export const useDeleteTasks = ({
  getTasksByIds,
  updateTasksAsync,
  updateGoal,
  onTaskDeleted,
}: UseDeleteTasksOptions) =>
  useCallback(
    async (taskIds: ID[], updateGoals?: boolean) => {
      if (!taskIds.length) {
        return;
      }

      const tasks = await getTasksByIds(taskIds);
      if (!tasks.length) {
        return;
      }

      const relatedIds = collectRelatedTaskIds(tasks);
      const relatedTasks = relatedIds.length
        ? await getTasksByIds(relatedIds)
        : [];
      const relatedById = new Map<ID, Task>(
        relatedTasks.map((task) => [task.id, task]),
      );

      const { taskUpdates, goalAdjustments, deletedTasks } =
        buildTaskDeletionUpdates({
          tasks,
          relatedById,
          updateGoals: !!updateGoals,
          deletedAt: new Date(),
        });

      deletedTasks.forEach((task) => onTaskDeleted?.(task));

      if (taskUpdates.length) {
        await updateTasksAsync(taskUpdates);
      }

      if (!updateGoal) {
        return;
      }
      Object.entries(goalAdjustments).forEach(
        ([goalId, { taskCount, completedTaskCount }]) => {
          updateGoal({
            goal: { id: goalId, taskCount, completedTaskCount },
          });
        },
      );
    },
    [getTasksByIds, updateTasksAsync, updateGoal, onTaskDeleted],
  );
